import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash, UserPlus, Key } from "@phosphor-icons/react";
import { fmtDate } from "../lib/utils";
import ConfirmDialog from "../components/crm/ConfirmDialog";

const DELETE_CONFIRM_MSG =
  "Deleting this agent will move all assigned leads to Unassigned. Related activities will remain linked to their leads.";

export default function Agents() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "agent" });
  const [saving, setSaving] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwForm, setPwForm] = useState({ password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("All fields required");
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm({ email: "", password: "", name: "", role: "agent" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      const { data } = await api.delete(`/users/${delTarget.id}`);
      const moved = data?.leads_unassigned ?? 0;
      toast.success(
        moved > 0
          ? `User deleted · ${moved} lead${moved === 1 ? "" : "s"} moved to Unassigned`
          : "User deleted"
      );
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    try {
      await api.put(`/users/${u.id}`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openResetPassword = (u) => {
    setPwTarget(u); setPwForm({ password: "", confirm: "" });
  };

  const submitPassword = async () => {
    if (!pwForm.password || pwForm.password.length < 6)
      return toast.error("Password must be at least 6 characters");
    if (pwForm.password !== pwForm.confirm)
      return toast.error("Passwords do not match");
    setPwSaving(true);
    try {
      await api.put(`/users/${pwTarget.id}`, { password: pwForm.password });
      toast.success("Password updated successfully.");
      setPwTarget(null); setPwForm({ password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setPwSaving(false); }
  };

  // Shared inline-row controls (used by desktop table cells AND mobile cards)
  const RoleSelect = ({ u, isSelf }) => (
    <Select
      value={u.role}
      onValueChange={(v) => changeRole(u, v)}
      disabled={isSelf}
    >
      <SelectTrigger className="w-28 h-8" data-testid={`role-select-${u.id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="agent">Agent</SelectItem>
        <SelectItem value="admin">Admin</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Team</div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">Agents &amp; Admins</h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">Manage roles and reset passwords. You cannot change your own role from here — use Settings to update your own profile.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="add-agent-button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 rounded-md bg-[#E1A104] text-[#1A3A21] hover:bg-[#C98F03] font-semibold text-xs md:text-sm shrink-0">
          <UserPlus size={14} weight="bold" /> <span className="hidden sm:inline">New User</span><span className="sm:hidden">New</span>
        </button>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full" data-testid="agents-table">
          <thead className="bg-gray-50">
            <tr>
              {["Name", "Email", "Role", "Status", "Created", "Actions"].map((h) => (
                <th key={h} className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = me?.id === u.id;
              return (
                <tr key={u.id} className="border-t border-gray-100" data-testid={`agent-row-${u.id}`}>
                  <td className="py-3 px-4 text-sm font-semibold">
                    {u.name}{isSelf && <span className="ml-1 text-[10px] text-gray-400">(you)</span>}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{u.email}</td>
                  <td className="py-3 px-4 text-sm">
                    <RoleSelect u={u} isSelf={isSelf} />
                    {isSelf && (
                      <div className="text-[10px] text-gray-400 mt-1" data-testid={`self-role-helper-${u.id}`}>
                        You cannot change your own role.
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">{u.is_active ? "Active" : "Inactive"}</td>
                  <td className="py-3 px-4 text-sm text-gray-500">{fmtDate(u.created_at)}</td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => openResetPassword(u)}
                      data-testid={`reset-password-${u.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 text-gray-700 hover:bg-gray-50 mr-1"
                      title="Reset password"
                    >
                      <Key size={13} /> Reset password
                    </button>
                    <button
                      onClick={() => setDelTarget(u)}
                      disabled={isSelf}
                      data-testid={`delete-user-${u.id}`}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MOBILE COMPACT CARDS */}
      <div className="md:hidden space-y-3" data-testid="agents-cards">
        {users.map((u) => {
          const isSelf = me?.id === u.id;
          return (
            <div
              key={u.id}
              data-testid={`agent-card-${u.id}`}
              className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {u.name}
                    {isSelf && <span className="ml-1 text-[10px] text-gray-400">(you)</span>}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${u.is_active ? "bg-green-50 text-green-700 border border-green-200" : "bg-gray-100 text-gray-500 border border-gray-200"}`}>
                  {u.is_active ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 w-12">Role</span>
                <RoleSelect u={u} isSelf={isSelf} />
              </div>
              {isSelf && (
                <div className="text-[10px] text-gray-400" data-testid={`self-role-helper-${u.id}`}>
                  You cannot change your own role.
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => openResetPassword(u)}
                  data-testid={`reset-password-mobile-${u.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded border border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  <Key size={13} /> Reset password
                </button>
                <button
                  onClick={() => setDelTarget(u)}
                  disabled={isSelf}
                  data-testid={`delete-user-mobile-${u.id}`}
                  className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded border border-gray-200 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Trash size={13} /> Delete
                </button>
              </div>
              <div className="text-[10px] text-gray-400">Created {fmtDate(u.created_at)}</div>
            </div>
          );
        })}
      </div>

      {/* Create new user */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="agent-modal">
          <DialogHeader>
            <DialogTitle className="font-display">New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label>
              <Input data-testid="agent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label>
              <Input data-testid="agent-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password</Label>
              <Input data-testid="agent-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="agent-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="agent-save"
              className="bg-[#1A3A21] hover:bg-[#142C19] text-white">
              {saving ? "Saving..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password modal */}
      <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
        <DialogContent data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Reset password</DialogTitle>
          </DialogHeader>
          {pwTarget && (
            <div className="text-sm text-gray-600 mb-3">
              Set a new password for <b>{pwTarget.name}</b> ({pwTarget.email}). The user will need to use this new password on next sign-in.
            </div>
          )}
          <div className="space-y-3">
            <div><Label>New password</Label>
              <Input type="password" data-testid="reset-password-input"
                value={pwForm.password}
                onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })} />
            </div>
            <div><Label>Confirm password</Label>
              <Input type="password" data-testid="reset-password-confirm"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwTarget(null)}>Cancel</Button>
            <Button onClick={submitPassword} disabled={pwSaving}
              data-testid="reset-password-submit"
              className="bg-[#1A3A21] hover:bg-[#142C19] text-white">
              {pwSaving ? "Saving..." : "Save new password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete user confirmation — DEPLOY-2 */}
      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(o) => !o && setDelTarget(null)}
        title={delTarget ? `Delete ${delTarget.name}?` : "Delete user"}
        message={DELETE_CONFIRM_MSG}
        confirmLabel="Delete user"
        destructive
        onConfirm={confirmDelete}
      />
    </div>
  );
}
